import React, { useEffect, useState, useRef } from "react";
import {
  ArrowLeft, Plus, Trash2, Save, Lightbulb, ChevronDown, ChevronRight, Wand2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getBudget, updateBudget, computeBudget, prefillSuggestions, assistantDraft } from "@/utils/budgetApi";
import { uid } from "@/utils/uid";
import { assistantLineKey, assistantLineToBudgetLine } from "@/utils/budgetAssistant";
import BudgetResults from "@/components/budget/BudgetResults";
import BudgetCashSummary from "@/components/budget/BudgetCashSummary";

const CATEGORIES = [
  "Ingresos", "Personal", "Herramientas y suscripciones",
  "Servicios externos", "Comercial", "Extraordinarios", "Fiscalidad",
];

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n ?? 0);

const isZero = (n) => Math.abs(n ?? 0) < 0.005;
// Tono para "Resultado del mes" / "En el banco": rojo en negativo, atenuado en cero.
const resultTone = (n) => {
  if (n == null) return "text-slate-400";
  if (isZero(n)) return "text-slate-300";
  return n < 0 ? "text-red-700" : "text-slate-800";
};

// Etiqueta de mes corta para la cabecera: "ene 26".
const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function monthLabel(mo) {
  if (!mo) return "";
  const m = parseInt(mo.slice(5, 7), 10);
  const y = mo.slice(2, 4);
  return `${MONTH_NAMES[m - 1] ?? mo} ${y}`;
}

// Desglose "ene 26: 800,00 € · feb 26: …" para el tooltip de partidas variables
// del asistente (estacionalidad real, no un mismo importe todos los meses).
function monthlyBreakdown(ma) {
  return Object.entries(ma ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([mo, v]) => `${monthLabel(mo)}: ${eur(v)}`)
    .join(" · ");
}

function newLine(kind = "expense") {
  // Las partidas nuevas nacen ya como rejilla (variable + monthly_amounts vacío).
  return {
    id: uid(),
    name: "", kind,
    category: kind === "income" ? "Ingresos" : "Servicios externos",
    pattern: "variable", month: null, amount: null, monthly_amounts: {}, scenario_overrides: null,
  };
}

function applyRange(type, { year, quarter, month }) {
  if (type === "annual")
    return { range_type: "annual", start_month: `${year}-01`, end_month: `${year}-12` };
  if (type === "quarter") {
    const s = (quarter - 1) * 3 + 1;
    return {
      range_type: "quarter",
      start_month: `${year}-${String(s).padStart(2, "0")}`,
      end_month: `${year}-${String(s + 2).padStart(2, "0")}`,
    };
  }
  if (type === "month")
    return { range_type: "month", start_month: month, end_month: month };
  return { range_type: "custom" };
}

function monthsBetween(start, end) {
  if (!start || !end) return [];
  const out = [];
  let [y, m] = [parseInt(start.slice(0, 4)), parseInt(start.slice(5, 7))];
  const [ey, em] = [parseInt(end.slice(0, 4)), parseInt(end.slice(5, 7))];
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
}

const SCENARIOS = [
  { value: "optimistic", label: "Optimista" },
  { value: "medium", label: "Medio" },
  { value: "pessimistic", label: "Pesimista" },
];

// Importe efectivo del MEDIO (base) de una línea en un mes.
function mediumMonthlyValue(line, month) {
  if (line.monthly_amounts && line.monthly_amounts[month] != null) return line.monthly_amounts[month];
  if (line.pattern === "recurring") return line.amount ?? 0;
  if (line.pattern === "one_off") return line.month === month ? (line.amount ?? 0) : 0;
  return 0;
}

// ¿Tiene la línea un valor PROPIO (override) en este escenario y mes?
function hasScenarioOverride(line, month, scenario) {
  if (scenario === "medium") return false;
  const ov = line.scenario_overrides?.[scenario]?.monthly_amounts;
  return !!(ov && ov[month] != null);
}

// Importe efectivo de una línea en un mes para el escenario dado.
// En optimista/pesimista, si no hay override propio del mes, hereda el medio.
function lineMonthlyValue(line, month, scenario = "medium") {
  if (scenario !== "medium") {
    const ov = line.scenario_overrides?.[scenario]?.monthly_amounts;
    if (ov && ov[month] != null) return ov[month];
    // Factor del escenario (p.ej. tipo de cambio USD): escala el Medio. Como es
    // relativo al Medio, hereda sus cambios (0 en el Medio → 0 aquí).
    const factor = line.scenario_factors?.[scenario];
    if (factor != null) return Math.round(mediumMonthlyValue(line, month) * factor * 100) / 100;
  }
  return mediumMonthlyValue(line, month);
}

export default function BudgetEditor({ budgetId, onBack }) {
  const [budget, setBudget] = useState(null);
  const [computed, setComputed] = useState(null);
  const [openBlocks, setOpenBlocks] = useState({ income: true, expense: true });
  const [scenario, setScenario] = useState("medium");
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSugg, setLoadingSugg] = useState(false);

  // Asistente IA (ítem 3.5): borrador determinista {consent, year, lines}.
  const [aiDraft, setAiDraft] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [loadingAssistant, setLoadingAssistant] = useState(false);

  // estado local del selector de rango (derivado de budget al cargarlo)
  const [rangeType, setRangeType] = useState("annual");
  const [rangeYear, setRangeYear] = useState(new Date().getFullYear());
  const [rangeQuarter, setRangeQuarter] = useState(1);
  const [rangeMonth, setRangeMonth] = useState(`${new Date().getFullYear()}-01`);

  useEffect(() => {
    getBudget(budgetId).then((b) => {
      setBudget(b);
      setComputed(b.computed);
      const rt = b.range_type ?? "annual";
      setRangeType(rt);
      if (b.start_month) {
        const y = parseInt(b.start_month.slice(0, 4));
        const m = parseInt(b.start_month.slice(5, 7));
        setRangeYear(y);
        setRangeMonth(b.start_month);
        if (rt === "quarter") setRangeQuarter(Math.ceil(m / 3));
      }
    });
  }, [budgetId]);

  function stripForApi(b) {
    const { computed: _c, ...rest } = b;
    return rest;
  }

  // recompute en vivo con debounce 350ms y descarte de respuestas obsoletas
  const reqId = useRef(0);
  useEffect(() => {
    if (!budget) return;
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const c = await computeBudget(stripForApi(budget));
        if (id === reqId.current) setComputed(c);
      } catch (_) {}
    }, 350);
    return () => clearTimeout(t);
  }, [budget]);

  function patch(updater) {
    setBudget((prev) => updater({ ...prev }));
  }

  const setField = (k, v) => patch((b) => ({ ...b, [k]: v }));
  const addLine = (kind) => patch((b) => ({ ...b, lines: [...b.lines, newLine(kind)] }));
  const removeLine = (id) => patch((b) => ({ ...b, lines: b.lines.filter((l) => l.id !== id) }));
  const updateLine = (id, k, v) =>
    patch((b) => ({ ...b, lines: b.lines.map((l) => (l.id === id ? { ...l, [k]: v } : l)) }));

  // Escribe un importe en un mes concreto de una partida, para el escenario activo.
  // - Medio: escribe en monthly_amounts base (migrando la línea a "variable" la 1ª vez).
  // - Optimista/Pesimista: escribe un override de ese escenario SOLO para ese mes; si el
  //   valor coincide con el medio, se elimina el override (vuelve a heredar el medio).
  function setLineMonth(lineId, month, value, sc) {
    patch((b) => ({
      ...b,
      lines: b.lines.map((l) => {
        if (l.id !== lineId) return l;
        const num = value === "" ? 0 : Number(value);
        if (sc === "medium") {
          const months = monthsBetween(b.start_month, b.end_month);
          // Si la partida aún no tiene importes por mes (p.ej. una sugerencia
          // recurrente, que vale lo mismo todos los meses), expandimos primero el
          // valor del Medio a TODOS los meses. Si no, al migrar a "variable" solo
          // quedaría definida la celda editada y el resto se iría a 0.
          const hasMa = l.monthly_amounts && Object.keys(l.monthly_amounts).length > 0;
          const base = hasMa
            ? l.monthly_amounts
            : Object.fromEntries(months.map((mo) => [mo, mediumMonthlyValue(l, mo)]));
          return { ...l, pattern: "variable", amount: null, month: null, monthly_amounts: { ...base, [month]: num } };
        }
        const medVal = mediumMonthlyValue(l, month);
        const factor = l.scenario_factors?.[sc];
        const inheritedVal = factor != null ? Math.round(medVal * factor * 100) / 100 : medVal;
        const so = { ...(l.scenario_overrides ?? {}) };
        const sceObj = { ...(so[sc] ?? {}) };
        const ma = { ...(sceObj.monthly_amounts ?? {}) };
        if (num === inheritedVal) delete ma[month];   // igual al valor heredado → hereda
        else ma[month] = num;
        if (Object.keys(ma).length) sceObj.monthly_amounts = ma;
        else delete sceObj.monthly_amounts;
        if (Object.keys(sceObj).length) so[sc] = sceObj;
        else delete so[sc];
        return { ...l, scenario_overrides: Object.keys(so).length ? so : null };
      }),
    }));
  }

  // Atajos de relleno por partida.
  function fillAll(lineId, value) {
    patch((b) => ({
      ...b,
      lines: b.lines.map((l) => l.id === lineId
        ? {
            ...l, pattern: "variable", amount: null, month: null,
            monthly_amounts: Object.fromEntries(
              monthsBetween(b.start_month, b.end_month).map((mo) => [mo, Number(value) || 0])
            ),
          }
        : l),
    }));
  }
  function growPct(lineId, pct) {
    patch((b) => ({
      ...b,
      lines: b.lines.map((l) => {
        if (l.id !== lineId) return l;
        const months = monthsBetween(b.start_month, b.end_month);
        let v = lineMonthlyValue(l, months[0]) || 0;
        const ma = {};
        months.forEach((mo, i) => {
          ma[mo] = Math.round((i === 0 ? v : (v = v * (1 + pct / 100))) * 100) / 100;
        });
        return { ...l, pattern: "variable", amount: null, month: null, monthly_amounts: ma };
      }),
    }));
  }
  function copyPrev(lineId) {
    patch((b) => ({
      ...b,
      lines: b.lines.map((l) => {
        if (l.id !== lineId) return l;
        const months = monthsBetween(b.start_month, b.end_month);
        const ma = {};
        let prev = 0;
        months.forEach((mo, i) => {
          const cur = l.monthly_amounts?.[mo] ?? lineMonthlyValue(l, mo);
          // Cada mes vacío hereda el valor del anterior.
          const val = i === 0 ? cur : (cur === 0 || cur == null ? prev : cur);
          ma[mo] = val;
          prev = val;
        });
        return { ...l, pattern: "variable", amount: null, month: null, monthly_amounts: ma };
      }),
    }));
  }
  // Atajo de tabla: rellena los meses vacíos de TODAS las partidas copiando el
  // valor del mes anterior (igual que copyPrev, pero sobre toda la rejilla).
  function copyPrevAll() {
    patch((b) => {
      const months = monthsBetween(b.start_month, b.end_month);
      return {
        ...b,
        lines: b.lines.map((l) => {
          const ma = {};
          let prev = 0;
          months.forEach((mo, i) => {
            const cur = l.monthly_amounts?.[mo] ?? lineMonthlyValue(l, mo);
            const val = i === 0 ? cur : (cur === 0 || cur == null ? prev : cur);
            ma[mo] = val;
            prev = val;
          });
          return { ...l, pattern: "variable", amount: null, month: null, monthly_amounts: ma };
        }),
      };
    });
  }

  // Convierte una sugerencia del backend en una partida de la rejilla.
  // - mensual: recurring (mismo importe cada mes; al editar migra a "variable").
  // - trimestral (impuestos): variable con el importe en los meses de pago
  //   (ene/abr/jul/oct) del rango, 0 en el resto.
  function suggestionToLine(s) {
    const base = {
      id: uid(), name: s.name, kind: s.kind, category: s.category,
      month: null, scenario_overrides: null,
      // Factores de cambio por escenario (USD): multiplican el Medio, de modo que
      // el valor de cada escenario sigue al Medio (incluido poner un mes a 0). Para
      // sugerencias en EUR no hay factores (s.fx_factors viene vacío).
      scenario_factors: s.fx_factors ?? null,
    };

    // Sugerencias con importes por mes (cobros pendientes, suscripciones
    // trimestrales/anuales): se colocan ya en su mes. Se recortan al rango del
    // presupuesto. Solo los cobros (carry_overdue) acumulan lo vencido antes del
    // inicio en el primer mes; una suscripción cuyo pago cae fuera de rango se
    // descarta (un pago pasado no se repite).
    if (s.monthly_amounts && Object.keys(s.monthly_amounts).length) {
      const range = monthsBetween(budget.start_month, budget.end_month);
      const first = range[0];
      const inRange = new Set(range);
      const ma = {};
      Object.entries(s.monthly_amounts).forEach(([mo, val]) => {
        const target = inRange.has(mo)
          ? mo
          : (s.carry_overdue && mo < budget.start_month ? first : null);
        if (!target) return;
        ma[target] = (ma[target] ?? 0) + val;
      });
      return { ...base, pattern: "variable", amount: null, monthly_amounts: ma };
    }
    if (s.frequency === "quarterly") {
      const ma = {};
      monthsBetween(budget.start_month, budget.end_month).forEach((mo) => {
        const m = parseInt(mo.slice(5, 7), 10);
        ma[mo] = [1, 4, 7, 10].includes(m) ? s.amount : 0;
      });
      return { ...base, pattern: "variable", amount: null, monthly_amounts: ma };
    }
    // Recurrente mensual: el Medio es `amount`; el factor de cambio (si lo hay)
    // lo escala por escenario sin romper la herencia.
    return { ...base, pattern: "recurring", amount: s.amount, monthly_amounts: {} };
  }

  async function loadSuggestions() {
    setLoadingSugg(true);
    try {
      const { lines } = await prefillSuggestions();
      setSuggestions(lines || []);
      setShowSuggestions(true);
    } finally {
      setLoadingSugg(false);
    }
  }

  // Añade una sugerencia a la rejilla y la retira del panel (no se impone nada).
  function addSuggestion(s) {
    patch((b) => ({ ...b, lines: [...b.lines, suggestionToLine(s)] }));
    setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
  }

  function addAllSuggestions() {
    patch((b) => ({ ...b, lines: [...b.lines, ...suggestions.map(suggestionToLine)] }));
    setSuggestions([]);
  }

  // --- Asistente IA (ítem 3.5) ---
  // Pide el borrador determinista para el año del presupuesto en edición.
  // Con consent=false las partidas llegan igual (sin `why`): NO es un error.
  async function loadAssistant() {
    setLoadingAssistant(true);
    try {
      const year = (budget.start_month ?? "").slice(0, 4) || String(new Date().getFullYear());
      const draft = await assistantDraft(year);
      setAiDraft(draft);
      setShowAssistant(true);
    } catch (_) {
      toast.error("No se pudo generar la propuesta del asistente. Inténtalo de nuevo.");
    } finally {
      setLoadingAssistant(false);
    }
  }

  // Añade UNA partida propuesta a la rejilla (nada de "añadir todo": cada
  // línea es un acto del usuario). La línea queda con source:"ai".
  function addAssistantLine(l) {
    const line = assistantLineToBudgetLine(l, budget.start_month, budget.end_month);
    if (!line) {
      toast.error("Esta partida no tiene importes dentro del rango del presupuesto.");
      return;
    }
    patch((b) => ({ ...b, lines: [...b.lines, line] }));
  }

  async function handleSave() {
    setSaving(true);
    await updateBudget(budgetId, stripForApi(budget));
    setSaving(false);
  }

  if (!budget) return <div className="p-6 text-gray-400">Cargando…</div>;

  // Partidas IA ya presentes en el presupuesto (por name+category): su botón
  // pasa a "Añadida" deshabilitado; si se borra la línea de la rejilla, vuelve
  // a estar disponible.
  const aiAddedKeys = new Set(
    budget.lines.filter((l) => l.source === "ai").map(assistantLineKey)
  );

  const months = monthsBetween(budget.start_month, budget.end_month);
  const scData = computed?.scenarios?.[scenario] ?? null;
  const minCash = scData?.min_cash ?? null;
  const scTotals = scData?.totals ?? null;
  const cashByMonth = scData?.months
    ? Object.fromEntries(scData.months.map((m) => [m.month, m]))
    : {};
  const comparison = computed?.scenarios ?? null;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      {/* Cabecera */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Input
          className="md:col-span-2"
          value={budget.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder="Nombre"
        />
        {/* Selector de tipo de rango */}
        <div className="md:col-span-2 flex flex-wrap items-center gap-2">
          <select
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
            value={rangeType}
            onChange={(e) => {
              const t = e.target.value;
              setRangeType(t);
              if (t !== "custom") {
                const derived = applyRange(t, { year: rangeYear, quarter: rangeQuarter, month: rangeMonth });
                patch((b) => ({ ...b, ...derived }));
              }
            }}
          >
            <option value="annual">Anual</option>
            <option value="quarter">Trimestral</option>
            <option value="month">Mensual</option>
            <option value="custom">Personalizado</option>
          </select>

          {(rangeType === "annual" || rangeType === "quarter") && (
            <Input
              className="w-24"
              type="number"
              value={rangeYear}
              min={2020}
              max={2040}
              onChange={(e) => {
                const y = Number(e.target.value);
                setRangeYear(y);
                const derived = applyRange(rangeType, { year: y, quarter: rangeQuarter, month: rangeMonth });
                patch((b) => ({ ...b, ...derived }));
              }}
            />
          )}

          {rangeType === "quarter" && (
            <select
              className="border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
              value={rangeQuarter}
              onChange={(e) => {
                const q = Number(e.target.value);
                setRangeQuarter(q);
                const derived = applyRange("quarter", { year: rangeYear, quarter: q, month: rangeMonth });
                patch((b) => ({ ...b, ...derived }));
              }}
            >
              <option value={1}>T1</option>
              <option value={2}>T2</option>
              <option value={3}>T3</option>
              <option value={4}>T4</option>
            </select>
          )}

          {rangeType === "month" && (
            <Input
              className="w-36"
              type="month"
              value={rangeMonth}
              onChange={(e) => {
                const mo = e.target.value;
                setRangeMonth(mo);
                const derived = applyRange("month", { year: rangeYear, quarter: rangeQuarter, month: mo });
                patch((b) => ({ ...b, ...derived }));
              }}
            />
          )}

          {rangeType === "custom" && (
            <>
              <Input
                className="w-36"
                type="month"
                value={budget.start_month}
                onChange={(e) => setField("start_month", e.target.value)}
              />
              <span className="text-gray-400 text-sm">→</span>
              <Input
                className="w-36"
                type="month"
                value={budget.end_month}
                onChange={(e) => setField("end_month", e.target.value)}
              />
            </>
          )}
        </div>

        <label className="text-sm text-gray-600 md:col-span-2 flex items-center gap-2">
          Saldo inicial de caja:
          <Input
            className="w-40"
            type="number"
            value={budget.opening_balance ?? 0}
            onChange={(e) => setField("opening_balance", Number(e.target.value))}
          />
        </label>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Button
          variant="outline"
          onClick={() => (showSuggestions ? setShowSuggestions(false) : loadSuggestions())}
          disabled={loadingSugg}
          className="flex items-center gap-1"
          title="Tus cobros pendientes de clientes, suscripciones activas, cuota de autónomo e impuestos, calculados desde tus datos reales"
        >
          <Lightbulb className="w-4 h-4" /> {loadingSugg ? "Cargando…" : "Sugerencias"}
        </Button>
        <Button
          variant="outline"
          onClick={() => (showAssistant ? setShowAssistant(false) : loadAssistant())}
          disabled={loadingAssistant}
          className="flex items-center gap-1"
          title="Borrador de partidas calculado desde tu histórico real (medias y tendencias de los últimos meses). Tú decides cuáles añadir, una a una."
          data-testid="budget-assistant"
        >
          <Sparkles className="w-4 h-4" /> {loadingAssistant ? "Generando…" : "Asistente IA"}
        </Button>
        {scenario === "medium" && (
          <Button
            variant="outline"
            onClick={copyPrevAll}
            className="flex items-center gap-1"
            title="Rellena los meses vacíos de todas las partidas copiando el valor del mes anterior"
          >
            <Wand2 className="w-4 h-4" /> Rellenar huecos
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1 ml-auto"
        >
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      {/* Panel de sugerencias (no intrusivo: tú eliges qué añadir) */}
      {showSuggestions && (
        <div className="mb-4 rounded-lg border bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-1">
              <Lightbulb className="w-4 h-4 text-amber-500" /> Cobros pendientes, suscripciones e impuestos
            </div>
            <div className="flex items-center gap-3">
              {suggestions.length > 0 && (
                <button onClick={addAllSuggestions} className="text-xs text-slate-600 hover:text-slate-900 underline">
                  Añadir todas
                </button>
              )}
              <button onClick={() => setShowSuggestions(false)} className="text-xs text-gray-400 hover:text-gray-600">
                Cerrar
              </button>
            </div>
          </div>
          {suggestions.length === 0 ? (
            <p className="text-sm text-gray-400">
              No hay sugerencias (o ya las has añadido). Se proponen tus cobros pendientes de clientes (en su mes de vencimiento), tus suscripciones activas (una a una), la cuota de autónomo y los impuestos, calculados desde tus datos reales.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {s.category}{s.hint ? ` · ${s.hint}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm tabular-nums ${s.kind === "income" ? "text-green-700" : "text-slate-700"}`}>
                      {eur(s.amount)}{s.frequency === "quarterly" ? "/trim." : s.frequency === "scheduled" ? "" : "/mes"}
                    </span>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => addSuggestion(s)}>
                      Añadir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Panel del Asistente IA (ítem 3.5): partidas DETERMINISTAS del histórico;
          la IA solo explica el porqué. Se añaden una a una, jamás en bloque. */}
      {showAssistant && aiDraft && (
        <div className="mb-4 rounded-lg border bg-white p-4" data-testid="budget-assistant-panel">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-blue-600" /> Propuesta del asistente{aiDraft.year ? ` · ${aiDraft.year}` : ""}
            </div>
            <button onClick={() => setShowAssistant(false)} className="text-xs text-gray-400 hover:text-gray-600">
              Cerrar
            </button>
          </div>
          {aiDraft.consent === false && (
            <p className="text-xs text-gray-500 mb-3">
              Activa la IA en Ajustes para ver la explicación de cada partida. Las propuestas son cálculos de tu histórico.
            </p>
          )}
          {(aiDraft.lines ?? []).length === 0 ? (
            <p className="text-sm text-gray-400">
              El asistente no ha encontrado partidas que proponer: necesita histórico de ingresos o gastos en los últimos 12 meses.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {(aiDraft.lines ?? []).map((l) => {
                const key = assistantLineKey(l);
                const added = aiAddedKeys.has(key);
                const hasMa = l.monthly_amounts && Object.keys(l.monthly_amounts).length > 0;
                const amountColor = l.kind === "income" ? "text-green-700" : "text-slate-700";
                return (
                  <div key={key} className="flex items-start justify-between gap-2 rounded border border-slate-200 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{l.name}</div>
                      <div className="text-xs text-gray-400 truncate">{l.category}</div>
                      {l.why && <p className="text-xs text-gray-500 mt-0.5">{l.why}</p>}
                      {l.note && <p className="text-xs text-gray-400 italic mt-0.5">{l.note}</p>}
                      {l.assumptions_label && (
                        <p className="text-[11px] text-amber-600 mt-0.5">{l.assumptions_label}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {hasMa ? (
                        <span
                          className={`text-sm tabular-nums cursor-help underline decoration-dotted ${amountColor}`}
                          title={monthlyBreakdown(l.monthly_amounts)}
                        >
                          variable
                        </span>
                      ) : (
                        <span className={`text-sm tabular-nums ${amountColor}`}>
                          {eur(l.amount ?? 0)}/mes
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        disabled={added}
                        onClick={() => addAssistantLine(l)}
                      >
                        {added ? "Añadida" : "Añadir"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Selector de escenario */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-gray-500">Escenario:</span>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {SCENARIOS.map((s) => (
            <button
              key={s.value}
              onClick={() => setScenario(s.value)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                scenario === s.value
                  ? s.value === "optimistic"
                    ? "bg-green-600 text-white"
                    : s.value === "pessimistic"
                    ? "bg-red-600 text-white"
                    : "bg-slate-800 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {scenario !== "medium" && (
          <span className="text-xs text-gray-400">
            Edita solo las celdas que cambien; el resto hereda el Medio.
          </span>
        )}
      </div>

      {/* Resumen de caja en vivo: punto más bajo, déficit y colchón (escenario activo) */}
      <BudgetCashSummary
        scData={scData}
        months={months}
        openingBalance={budget.opening_balance ?? 0}
        scenarioLabel={SCENARIOS.find((s) => s.value === scenario)?.label ?? ""}
      />

      {/* Rejilla mensual */}
      {months.length === 0 ? (
        <p className="text-gray-400 mb-8">Elige un rango de meses para empezar.</p>
      ) : (
        <div className="border rounded-lg bg-white overflow-x-auto mb-8">
          <table className="text-sm border-collapse min-w-full">
            <thead>
              <tr className="text-gray-500 border-b bg-slate-50">
                <th className="sticky left-0 z-10 bg-slate-50 text-left font-medium px-3 py-2 min-w-[240px]">
                  Partida
                </th>
                {months.map((mo) => (
                  <th key={mo} className="text-right font-medium px-2 py-2 min-w-[90px] whitespace-nowrap">
                    {monthLabel(mo)}
                  </th>
                ))}
                <th className="text-right font-semibold px-3 py-2 min-w-[100px] whitespace-nowrap">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <Block
                kind="income"
                title="Lo que entra"
                budget={budget}
                months={months}
                open={openBlocks.income}
                onToggle={() => setOpenBlocks((o) => ({ ...o, income: !o.income }))}
                onAdd={() => addLine("income")}
                scenario={scenario}
                suggestions={suggestions}
                updateLine={updateLine}
                removeLine={removeLine}
                setLineMonth={setLineMonth}
                fillAll={fillAll}
                growPct={growPct}
                copyPrev={copyPrev}
              />
              <Block
                kind="expense"
                title="Lo que sale"
                budget={budget}
                months={months}
                open={openBlocks.expense}
                onToggle={() => setOpenBlocks((o) => ({ ...o, expense: !o.expense }))}
                onAdd={() => addLine("expense")}
                scenario={scenario}
                suggestions={suggestions}
                updateLine={updateLine}
                removeLine={removeLine}
                setLineMonth={setLineMonth}
                fillAll={fillAll}
                growPct={growPct}
                copyPrev={copyPrev}
              />

              {/* Filas calculadas */}
              <tr className="border-t-2 border-slate-300 font-semibold">
                <td className="sticky left-0 z-10 bg-white px-3 py-2">Resultado del mes</td>
                {months.map((mo) => {
                  const r = cashByMonth[mo]?.result;
                  return (
                    <td key={mo} className={`text-right px-2 py-2 tabular-nums ${resultTone(r)}`}>
                      {r != null ? eur(r) : "—"}
                    </td>
                  );
                })}
                <td className="text-right px-3 py-2 tabular-nums">
                  {scTotals?.net != null ? eur(scTotals.net) : "—"}
                </td>
              </tr>
              <tr className="font-bold bg-slate-100 border-t border-slate-300">
                <td className="sticky left-0 z-10 bg-slate-100 px-3 py-2.5">En el banco</td>
                {months.map((mo) => {
                  const c = cashByMonth[mo]?.cash;
                  const isMin = minCash?.negative && minCash.month === mo;
                  return (
                    <td
                      key={mo}
                      className={`text-right px-2 py-2.5 tabular-nums ${
                        isMin ? "bg-red-100 text-red-700 font-bold rounded" : resultTone(c)
                      }`}
                    >
                      {c != null ? eur(c) : "—"}
                    </td>
                  );
                })}
                <td className="text-right px-3 py-2.5 tabular-nums">
                  {scTotals?.cash_final != null ? eur(scTotals.cash_final) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Comparativa de los 3 escenarios */}
      {comparison && (
        <div className="mb-6 rounded-lg border bg-white p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">Comparativa de escenarios</div>
          <table className="text-sm w-full max-w-md">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium py-1" />
                <th className="text-right font-medium py-1">Resultado</th>
                <th className="text-right font-medium py-1">En el banco (final)</th>
              </tr>
            </thead>
            <tbody>
              {SCENARIOS.map((s) => {
                const t = comparison[s.value]?.totals;
                const active = s.value === scenario;
                return (
                  <tr key={s.value} className={active ? "font-semibold bg-slate-50" : ""}>
                    <td className="py-1">
                      <button onClick={() => setScenario(s.value)} className="hover:underline">
                        {s.label}{active ? " ←" : ""}
                      </button>
                    </td>
                    <td className={`text-right py-1 tabular-nums ${t && t.net < 0 ? "text-red-600" : ""}`}>
                      {t ? eur(t.net) : "—"}
                    </td>
                    <td className={`text-right py-1 tabular-nums ${t && t.cash_final < 0 ? "text-red-600" : ""}`}>
                      {t ? eur(t.cash_final) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Gráfico de caja como apoyo */}
      {computed && <BudgetResults computed={computed} />}
    </div>
  );
}

// Bloque colapsable ("Lo que entra" / "Lo que sale") con sus categorías,
// filas de partida editables, subtotal por mes y botón de añadir partida.
function Block({
  kind, title, budget, months, open, onToggle, onAdd, scenario, suggestions,
  updateLine, removeLine, setLineMonth, fillAll, growPct, copyPrev,
}) {
  const lines = budget.lines.filter((l) => l.kind === kind);
  const colSpan = months.length + 2;
  const isIncome = kind === "income";

  // Orden de categorías: las conocidas primero, luego las presentes que no estén.
  const present = [...new Set(lines.map((l) => l.category).filter(Boolean))];
  const orderedCats = [...CATEGORIES, ...present.filter((c) => !CATEGORIES.includes(c))];
  const grouped = orderedCats
    .map((cat) => ({ cat, lines: lines.filter((l) => l.category === cat) }))
    .filter((g) => g.lines.length > 0);

  // Subtotal del bloque por mes (en el escenario activo).
  const subtotalByMonth = months.map((mo) =>
    lines.reduce((acc, l) => acc + lineMonthlyValue(l, mo, scenario), 0)
  );
  const subtotalTotal = subtotalByMonth.reduce((a, b) => a + b, 0);

  return (
    <>
      {/* Cabecera del bloque */}
      <tr className="border-t bg-slate-100">
        <td colSpan={colSpan} className="px-3 py-2">
          <button
            onClick={onToggle}
            className="flex items-center gap-1 font-semibold text-slate-700"
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {title}
          </button>
        </td>
      </tr>

      {open && (
        <>
          {grouped.map((g) => (
            <React.Fragment key={g.cat}>
              {/* Subcabecera de categoría */}
              <tr>
                <td
                  colSpan={colSpan}
                  className="sticky left-0 bg-white px-3 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                >
                  {g.cat}
                </td>
              </tr>
              {g.lines.map((l) => (
                <LineRow
                  key={l.id}
                  line={l}
                  months={months}
                  isIncome={isIncome}
                  scenario={scenario}
                  suggestions={suggestions}
                  updateLine={updateLine}
                  removeLine={removeLine}
                  setLineMonth={setLineMonth}
                  fillAll={fillAll}
                  growPct={growPct}
                  copyPrev={copyPrev}
                />
              ))}
            </React.Fragment>
          ))}

          {/* Añadir partida */}
          <tr>
            <td colSpan={colSpan} className="px-3 py-2 sticky left-0 bg-white">
              <button
                onClick={onAdd}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
              >
                <Plus className="w-4 h-4" /> Añadir partida
              </button>
            </td>
          </tr>

          {/* Subtotal del bloque */}
          <tr className="bg-slate-50 text-slate-600 border-t">
            <td className="sticky left-0 z-10 bg-slate-50 px-3 py-1.5 text-sm font-medium">
              Subtotal {title.toLowerCase()}
            </td>
            {subtotalByMonth.map((v, i) => (
              <td key={months[i]} className="text-right px-2 py-1.5 tabular-nums text-sm">
                {eur(v)}
              </td>
            ))}
            <td className="text-right px-3 py-1.5 tabular-nums text-sm font-semibold">
              {eur(subtotalTotal)}
            </td>
          </tr>
        </>
      )}
    </>
  );
}

// Una fila de partida: nombre + categoría + menú de atajos + papelera en la
// primera columna (sticky), y un input numérico por mes.
function LineRow({
  line, months, isIncome, scenario, suggestions, updateLine, removeLine, setLineMonth, fillAll, growPct, copyPrev,
}) {
  const total = months.reduce((acc, mo) => acc + lineMonthlyValue(line, mo, scenario), 0);
  const amountColor = isIncome ? "text-green-700" : "text-slate-700";

  // Sugerido inline: si hay una sugerencia para esta categoría y la partida está
  // vacía, ofrecer su importe (solo en el escenario Medio, que es la base editable).
  const suggestion =
    scenario === "medium" && total === 0 && line.category
      ? (suggestions || []).find(
          (s) =>
            s.kind === (isIncome ? "income" : "expense") &&
            s.category === line.category &&
            s.frequency !== "quarterly" &&
            s.frequency !== "scheduled"
        )
      : null;

  return (
    <tr className="border-b last:border-0 hover:bg-slate-50/60">
      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 group-hover:bg-slate-50">
        <div className="flex items-center gap-1.5">
          <Input
            className="h-8 flex-1 min-w-[150px] text-sm"
            value={line.name}
            placeholder="Concepto"
            onChange={(e) => updateLine(line.id, "name", e.target.value)}
          />
          <Input
            list={`cats-${line.id}`}
            className="h-8 w-32 text-xs"
            value={line.category}
            placeholder="Categoría"
            onChange={(e) => updateLine(line.id, "category", e.target.value)}
          />
          <datalist id={`cats-${line.id}`}>
            {CATEGORIES.map((c) => <option key={c} value={c} />)}
          </datalist>
          {scenario === "medium" && (
            <RowMenu
              line={line}
              fillAll={fillAll}
              growPct={growPct}
              copyPrev={copyPrev}
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-700 shrink-0"
            onClick={() => removeLine(line.id)}
            title="Borrar partida"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        {suggestion && (
          <button
            type="button"
            onClick={() => fillAll(line.id, suggestion.amount)}
            className="mt-0.5 text-[11px] text-amber-600 hover:underline"
            title="Aplicar a todos los meses"
          >
            Sugerido: {eur(suggestion.amount)}/mes
          </button>
        )}
      </td>
      {months.map((mo) => {
        // En optimista/pesimista, una celda sin override propio hereda el Medio
        // (se muestra atenuada para distinguir lo que has cambiado de lo heredado).
        const inherited = scenario !== "medium" && !hasScenarioOverride(line, mo, scenario);
        const hasFactor = inherited && line.scenario_factors?.[scenario] != null;
        const v = lineMonthlyValue(line, mo, scenario);
        return (
          <td key={mo} className="px-1 py-1">
            <input
              type="number"
              value={v}
              onChange={(e) => setLineMonth(line.id, mo, e.target.value, scenario)}
              title={inherited
                ? (hasFactor
                    ? "Convertido del Medio al tipo de cambio de este escenario (cámbialo para fijarlo)"
                    : "Heredado del Medio (cámbialo para fijar este escenario)")
                : undefined}
              className={`w-full min-w-[80px] h-8 rounded border bg-transparent px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-900 ${
                inherited
                  ? "border-slate-200 text-gray-400 italic"
                  : `border-slate-300 ${isZero(v) ? "text-slate-300" : amountColor}`
              }`}
            />
          </td>
        );
      })}
      <td className={`text-right px-3 py-1.5 tabular-nums font-medium ${amountColor}`}>
        {eur(total)}
      </td>
    </tr>
  );
}

// Menú de atajos de relleno por partida.
function RowMenu({ line, fillAll, growPct, copyPrev }) {
  const [fillValue, setFillValue] = useState("");
  const [pctValue, setPctValue] = useState("");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-2 text-slate-500 hover:text-slate-800 shrink-0"
          title="Atajos de relleno: rellenar todos los meses, crecer cada mes %, copiar mes anterior"
        >
          <Wand2 className="w-3.5 h-3.5" />
          <span className="text-xs">Rellenar</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2">
        <DropdownMenuLabel className="px-1 pb-1 text-xs text-gray-500">
          Rellenar todos los meses
        </DropdownMenuLabel>
        <div
          className="flex items-center gap-1 px-1 pb-1"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            type="number"
            className="h-8 text-sm"
            placeholder="Importe"
            value={fillValue}
            onChange={(e) => setFillValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            size="sm"
            className="h-8 shrink-0"
            onClick={() => { if (fillValue !== "") fillAll(line.id, fillValue); }}
          >
            Aplicar
          </Button>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="px-1 pb-1 text-xs text-gray-500">
          Crecer cada mes
        </DropdownMenuLabel>
        <div
          className="flex items-center gap-1 px-1 pb-1"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            type="number"
            className="h-8 text-sm"
            placeholder="% mensual"
            value={pctValue}
            onChange={(e) => setPctValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8 shrink-0"
            onClick={() => { if (pctValue !== "") growPct(line.id, Number(pctValue)); }}
          >
            Aplicar
          </Button>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => copyPrev(line.id)}>
          Copiar mes anterior en los vacíos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
